// `/admin/kyc/$id` (`spec/17-frontend.md` §8.2). Foco declarado: **os documentos**.
//
// Desde esta task o componente é o CONTEÚDO do `RouteDrawer` da rota-pai (croqui: frame
// `SubmissaoKyc`): o cabeçalho (nome do seller, id em mono e o botão de fechar) é do
// drawer, e a fila continua desenhada embaixo, inclusive em deep link.
//
// Dois caminhos de step-up, e nenhum deles reimplementa o dialog:
// 1. Ver documento, o GET responde 200 SEM `files[].view_url` (§4.3, step-up condicional).
//    O botão chama `useStepUp()` e refaz o GET; não é erro, é convite.
// 2. Aprovar/reprovar, o `403 mfa_required` do POST é tratado em `api/client.ts`, que
//    abre o `StepUpDialog` e reexecuta a chamada.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { adminKycDetailQueryOptions, approveKyc, rejectKyc } from '../api/queries/admin-kyc.ts';
import { useStepUp } from '../app/step-up.tsx';
import { ErrorState } from '../components/app/error-state.tsx';
import { CardSkeleton } from '../components/app/skeletons.tsx';
import { Empty } from '../components/kit/empty.tsx';
import { ApproveDialog, RejectDialog } from '../features/admin-kyc/decision-dialogs.tsx';
import { isNotFound, isPartnerDecides } from '../features/admin-kyc/model.ts';
import { SubmissionDetail } from '../features/admin-kyc/submission-detail.tsx';

function AdminKycSubmission() {
  const { id } = Route.useParams();
  const queryClient = useQueryClient();
  const stepUp = useStepUp();

  const detail = useQuery(adminKycDetailQueryOptions(id));
  const [approveOpen, setApproveOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [revealing, setRevealing] = useState(false);

  /** A decisão muda a conta do seller e o `['kyc']` do painel inteiro. */
  const afterDecision = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['kyc'] });
    await detail.refetch();
  };

  /**
   * Fecha a confirmação mesmo no erro: o `409 partner_decides` e qualquer outra recusa
   * viram estado do drawer, que fica ATRÁS do dialog, deixá-lo aberto esconderia a
   * explicação do operador.
   */
  const closeDialogs = (): void => {
    setApproveOpen(false);
    setRejectOpen(false);
  };

  const approve = useMutation({
    mutationFn: () => approveKyc(id),
    onSuccess: afterDecision,
    onSettled: closeDialogs,
  });
  const reject = useMutation({
    mutationFn: (reason: string) => rejectKyc(id, reason),
    onSuccess: afterDecision,
    onSettled: closeDialogs,
  });

  const reveal = async (): Promise<void> => {
    setRevealing(true);
    try {
      if (await stepUp()) {
        await detail.refetch();
      }
    } finally {
      setRevealing(false);
    }
  };

  const sellerName = detail.data?.seller.display_name ?? 'este seller';

  return (
    <>
      {detail.isPending ? (
        <CardSkeleton lines={6} />
      ) : isNotFound(detail.error) ? (
        <Empty
          title="Submissão não encontrada"
          hint="Este KYC não existe mais ou o link está errado."
        />
      ) : detail.isError ? (
        <ErrorState
          error={detail.error}
          onRetry={() => {
            void detail.refetch();
          }}
        />
      ) : (
        <SubmissionDetail
          submission={detail.data}
          partnerDecides={isPartnerDecides(approve.error)}
          submitting={approve.isPending || reject.isPending}
          error={approve.error ?? reject.error}
          onApprove={() => {
            setApproveOpen(true);
          }}
          onReject={() => {
            setRejectOpen(true);
          }}
          onStepUp={() => {
            void reveal();
          }}
          revealing={revealing}
        />
      )}

      <ApproveDialog
        open={approveOpen}
        sellerName={sellerName}
        submitting={approve.isPending}
        onOpenChange={setApproveOpen}
        onConfirm={() => {
          approve.mutate();
        }}
      />
      <RejectDialog
        open={rejectOpen}
        sellerName={sellerName}
        submitting={reject.isPending}
        onOpenChange={setRejectOpen}
        onConfirm={(reason) => {
          reject.mutate(reason);
        }}
      />
    </>
  );
}

export const Route = createFileRoute('/admin/kyc/$id')({ component: AdminKycSubmission });
